import { FieldType, UIPermission, Column, FieldValue, FormatType, Badge, O, PanelWrapper, Filters, FilterField, option } from "../../../../../features/ui"
import { BookmarkButton } from "../../../../../features/bookmarks"
import { RoutePath } from "../../../../../features/routes"
import { DataType, TableName } from "../../../../../tables"
import { useSystemStateHooks } from "../../../../system"
import { Customer, CustomerCategory } from "../../../state/types"
import { Data2, Labels, useDataTypesHooks, DataTypeList2 } from "../../../../../features/data-types"
import { useCustomerLoaders } from "../../../loaders/customerLoaders"
import { firstItem } from "../../../../../utils/listUtils"
import { useCustomerTypeLoaders } from "../../../loaders/customerTypeLoaders"
import { Params } from "../../../../../state/reducers/types"
import { useMemo, useState } from "react"
import { useCustomerHooks } from "../../../hooks/customerHooks"

export const CustomerList = () => {
    const [searchText, setSearchText] = useState<string>()
    const [creditAccount, setCreditAccount] = useState<boolean>()
    const [customerTypeId, setCustomerTypeId] = useState<string>()
    const [category, setCategory] = useState<CustomerCategory>()

    const { userHasPermission } = useSystemStateHooks()
    const readOnly = !userHasPermission(UIPermission.SAVE_CUSTOMERS)
    const { loadFilteredBatch, countFiltered } = useCustomerHooks()
    const { formatCustomValue } = useDataTypesHooks()

    const customerLoaders = useCustomerLoaders()
    const customerTypeLoaders = useCustomerTypeLoaders()

    const filtersText = useMemo(() =>
        [searchText, creditAccount, customerTypeId, category].filter(Boolean).join(';')
    , [searchText, creditAccount, customerTypeId, category])

    const onSearch = (value?: FieldValue) => {
        const newSearchText = value as O<string>
        setSearchText(newSearchText)
    }

    const searchField: FilterField = {
        name: 'search',
        type: FieldType.TEXT,
        placeholder: 'Buscar por nombre, DNI / CUIT o email',
        icon: 'search',
        onChange: onSearch
    }

    const onChangeCreditAccount = (value?: FieldValue) => setCreditAccount(value as O<boolean> === true ? true : undefined)

    const onChangeCustomerTypeId = (value?: FieldValue) => setCustomerTypeId(value as O<string>)

    const onChangeCategory = (value?: FieldValue) => setCategory(value as O<CustomerCategory>)

    const filterFields: FilterField[] = [
        {
            name: 'creditAccount',
            type: FieldType.BOOLEAN,
            label: 'Cuenta Corriente',
            space: 2,
            onChange: onChangeCreditAccount
        },
        {
            name: 'customerTypeId',
            type: FieldType.SELECT,
            options: customerTypeLoaders().findAllCustomerTypes(true)
                .filter(customerType => !customerType.archived)
                .map(customerType => option(customerType.id, customerType.name)),
            label: 'Lista de Precios',
            onChange: onChangeCustomerTypeId
        },
        {
            name: 'category',
            type: FieldType.SELECT,
            options: Object.values(CustomerCategory).map(category => option(category, Labels.customerCategoryLabel(category))),
            label: 'Categoría',
            onChange: onChangeCategory
        }
    ]

    const columns: Column[] = [
        {
            name: 'name',
            type: FieldType.TEXT,
            label: 'Nombre'
        },
        {
            name: 'identifier',
            type: FieldType.TEXT,
            label: 'DNI / CUIT',
            render: (value?: FieldValue) => {
                const identifier = value as O<number>
                const format = identifier?.toString().length === 11 ? FormatType.CUIT_CUIL : FormatType.DNI
                return formatCustomValue(identifier, format) || <Badge label="Vacío" />
            }
        },
        {
            name: 'customerTypeId',
            type: FieldType.TABLE,
            table: TableName.CUSTOMER_TYPES,
            label: 'Lista de Precios',
            render: (value?: FieldValue) => {
                const customerType = customerTypeLoaders().findCustomerType(value! as string)!
                return <Badge label={customerType.name} />
            }
        }
    ]

    const loadBatch = async (startKey?: Params) => {
        if (filtersText) {
            return loadFilteredBatch(searchText, creditAccount, customerTypeId, category, startKey)
        } else {
            const { loadCustomersBatch } = customerLoaders()
            return loadCustomersBatch(startKey)
        }
    }

    const count = async (startKey?: Params) => {
        if (filtersText) {
            return countFiltered(searchText, creditAccount, customerTypeId, category, startKey)
        } else {
            const { countCustomers } = customerLoaders()
            return countCustomers(startKey, 1)
        }
    }

    const toRow = (customerDT: DataType): Data2 => {
        const { mainAddress, emails, phones, deliveryAddresses, creditAccount, ...data } = customerDT as Customer
        return {
            ...data,
            email: firstItem(emails),
            debt: customerDT.id
        }
    }

    return (
        <PanelWrapper
            title="Clientes"
            wide
            titleRight={<BookmarkButton routePath={RoutePath.CUSTOMERS} />}
        >
            <Filters
                filtersKey="customers"
                searchField={searchField}
                fields={filterFields}
            />
            <DataTypeList2
                columns={columns}
                filtersText={filtersText}
                readOnly={readOnly}
                toRow={toRow}
                loadBatch={loadBatch}
                count={count}
            />
        </PanelWrapper>
    )
}
